import React, { useState, useCallback, useMemo } from 'react';
import { YStack, useTheme } from 'tamagui';

import { CommentImage, isHidden } from '@cobu/types';
import { COMMENT_IMAGE_CONSTANTS } from '../../../constants';
import ImageViewerModal from '../../ui/ImageViewerModal';
import { CobuView } from '@cobu/ui';
import { Virtuoso } from 'react-virtuoso';
import { logger } from '../../../utils/logger';
import {
  HiddenImageBadge,
  HiddenImageNoticeBlock,
  useHiddenImageNotice,
} from './HiddenImageNotice';

interface CommentImageLayoutProps {
  images: CommentImage[];
  isParentComment?: boolean;
  containerWidth?: number | string;
  isAuthor?: boolean;
}

interface ImageDimensions {
  width: number | string;
  height: number;
}

export default function CommentImageLayout({
  images,
  isParentComment = true,
  containerWidth, // Default container width
  isAuthor = false
}: CommentImageLayoutProps) {
  const theme = useTheme();
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const hiddenNotice = useHiddenImageNotice();

  // Calculate image dimensions based on comment type and count
  const imageDimensions = useMemo((): ImageDimensions => {
    const height = isParentComment
      ? COMMENT_IMAGE_CONSTANTS.PARENT_COMMENT_IMAGE_HEIGHT
      : COMMENT_IMAGE_CONSTANTS.CHILD_COMMENT_IMAGE_HEIGHT;

    if (images.length === 1) {
      if (isParentComment) {
        return {
          width: Math.floor(Number(containerWidth || 400) * 0.4),
          height: height,
        };
      }
      return {
        width: height, // Square dimensions for child comments
        height: height,
      };
    } else if (isParentComment) {
      // Parent comment with multiple images - use grid (40% width each)
      return {
        width: Math.floor(Number(containerWidth || 400) * 0.4),
        height: height,
      };
    } else {
      // Child comment with multiple images - use square horizontal scroll
      return {
        width: height, // Square dimensions
        height: height,
      };
    }
  }, [images.length, isParentComment, containerWidth]);

  // Handle image press to open viewer
  const handleImagePress = useCallback((index: number) => {
    setSelectedImageIndex(index);
    setImageViewerVisible(true);
  }, []);

  // Render individual image item
  const renderImageItem = useCallback(({ item, index }: { item: CommentImage; index: number }) => {
    // Handle both 'url' and legacy 'image' field names
    const imageUrl = item.url || (item as any).image;

    if (!imageUrl) {
      logger.warn('No image URL found for item:', item);
      return null;
    }

    return (
      <CobuView
        onPress={() => handleImagePress(index)}
      >
        <YStack
          style={[
            {
              position: 'relative',
              width: imageDimensions.width,
              height: imageDimensions.height,
              backgroundColor: theme.gray3?.val || '#f0f0f0',
            },
            { marginRight: index !== images.length - 1 ? 8 : 0 }
          ]}
        >
          <img
            src={imageUrl}
            style={{ width: imageDimensions.width, height: imageDimensions.height, borderRadius: COMMENT_IMAGE_CONSTANTS.IMAGE_BORDER_RADIUS, objectFit: 'cover' }}
          />

          {isAuthor && isHidden(item) && (
            <HiddenImageBadge
              uuid={item.uuid}
              isOpen={hiddenNotice.openId === item.uuid}
              onToggle={hiddenNotice.toggle}
              onHoverChange={hiddenNotice.setHoveredId}
            />
          )}
        </YStack>
      </CobuView>
    );
  }, [
    imageDimensions,
    handleImagePress,
    theme.gray3?.val,
    images.length,
    isAuthor,
    // Required, else the badge closes over a stale openId.
    hiddenNotice.openId,
    hiddenNotice.toggle,
    hiddenNotice.setHoveredId,
  ]);

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <YStack mt="$2">
      {images.length === 1 ? (
        // Single image - full width
        <YStack>
          {renderImageItem({ item: images[0], index: 0 })}
        </YStack>
      ) : isParentComment ? (
        <YStack style={{ width: containerWidth, overflow: 'hidden' }}>
          <Virtuoso
            data={images}
            itemContent={(index, item) => renderImageItem({ item: item as CommentImage, index })}
            horizontalDirection
            computeItemKey={(index: number, item: CommentImage) => {
              return `item-${item.uuid}`
            }}
            style={{ height: imageDimensions.height, width: '100%', overflowY: 'hidden' }}
          />
        </YStack>
      ) : (
        // Multiple images for child comments - horizontal scroll
        <YStack style={[
          { height: COMMENT_IMAGE_CONSTANTS.CHILD_COMMENT_IMAGE_HEIGHT + 16, overflow: 'hidden' }]}>
          <Virtuoso
            data={images}
            itemContent={(index, item) => renderImageItem({ item: item as CommentImage, index })}
            horizontalDirection
            computeItemKey={(index: number, item: CommentImage) => {
              return `item-${item.uuid}`
            }}
            style={{ height: imageDimensions.height, width: '100%', overflowY: 'hidden' }}
          />
        </YStack>
      )}

      {isAuthor && hiddenNotice.isActiveFor(images) && <HiddenImageNoticeBlock />}

      {/* Image viewer modal */}
      <ImageViewerModal
        visible={imageViewerVisible}
        images={images}
        initialIndex={selectedImageIndex}
        onClose={() => setImageViewerVisible(false)}
      />
    </YStack>
  );
}
